import { useNavigationItems } from '@src/hooks/useNavigationItems';
import { ThemeProvider } from '@src/styles/ThemeProvider';
import {
  MixpanelEvent,
  MixpanelEventProperty,
  MobileModalProvider,
  Stack,
  useEventListener,
  useMixpanel,
  usePreferredTheme,
} from '@talos/kyoko';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { useThemeTypePreference } from '../../hooks/useThemeTypePreference';
import { MobileOMS } from './MobileOMS';
import { MobileUnsupportedSectionPlaceholder } from './MobileUnsupportedSectionPlaceholder';
import { MobileTradingRoute } from './Trading/MobileTradingRoute';

const SUPPORTED_MOBILE_PAGE_COMPONENTS = {
  '/trading': <MobileTradingRoute />,
};

export const MobileStyles = createGlobalStyle`
  html, body {
    // Make sure the background behind the address bar is the same as the background content on mobile
    background: ${({ theme }) => theme.backgroundContent};
  }
`;

function isSupportedMobilePagePath(path: string): path is keyof typeof SUPPORTED_MOBILE_PAGE_COMPONENTS {
  return path in SUPPORTED_MOBILE_PAGE_COMPONENTS;
}

function MobilePages() {
  const { themeType } = useThemeTypePreference();
  const preferredTheme = usePreferredTheme('DarkTalosMobileTheme', themeType);
  const location = useLocation();
  const mixpanel = useMixpanel();

  const path = location?.pathname;
  const section = path.split('/')[1] ?? 'trading';

  useEffect(() => {
    mixpanel.time_event(MixpanelEvent.ViewSection);
    return () => {
      mixpanel.track(MixpanelEvent.ViewSection, { [MixpanelEventProperty.Section]: section });
    };
  }, [section, mixpanel]);

  useEventListener('beforeunload', () => {
    mixpanel.track(MixpanelEvent.ViewSection, { [MixpanelEventProperty.Section]: section });
  });

  return (
    <ThemeProvider theme={preferredTheme}>
      <MobileStyles />
      <MobileModalProvider>
        <InnerMobilePages />
      </MobileModalProvider>
    </ThemeProvider>
  );
}

function InnerMobilePages() {
  const { navItems } = useNavigationItems();

  return (
    <Stack overflow="auto" h="100vh" background="backgroundContent">
      <MobileOMS />
      <Routes>
        <Route path="/" element={<Navigate to="/trading" replace />} />
        {navItems.map(item => {
          if (item.path == null || item.label == null) {
            return null;
          }

          const element = isSupportedMobilePagePath(item.path) ? (
            SUPPORTED_MOBILE_PAGE_COMPONENTS[item.path]
          ) : (
            <MobileUnsupportedSectionPlaceholder label={item.label} />
          );

          return <Route key={item.path} path={`${item.path}/*`} element={element} />;
        })}
      </Routes>
    </Stack>
  );
}

export default MobilePages;
